import { EVERYONE, SABOTEURS, STOP_POINTS, taskDef } from '@continuum/show-core';
import type { EveningSummary, GameSet, Meeting, Profession, Reveal, StopReason, TaskState, WireChat } from '@continuum/show-core';
import { screenText } from './screenText.js';
import type { Lang } from './text.js';

/**
 * The evening wrapped, in numbers beyond the scores: the real ones and the funny ones, worked out on
 * the guest's own phone from what it already holds — the room's public state and its own `You`.
 *
 * What goes in: this guest's own games (time, mistakes), the stops' reveals (public once each stop has
 * revealed, with every award's reason), the meetings' verdicts (who was voted out), the alarms' public
 * traces (`near`), and this phone's own role and hidden signal. What never goes in: anybody's ballot.
 * Ballots never leave the room, so there is no "times suspected" and no who-voted-for-whom; a right vote
 * is only ever read off the reveal's own reasons. Nothing here is about another guest.
 */

export type Era = 1 | 2 | 3 | 4 | 5;

/** One finished game of this guest's, with its title in both languages. */
export interface GameRun {
  chapter: Era;
  index: number;
  title: Record<Lang, string>;
  /** The time the room accepted, or null from a phone that did not say. */
  ms: number | null;
  parMs: number;
  mistakes: number | null;
}

export interface WrappedStats {
  /** This guest's post, for the crew title ("THE CALM NAVIGATOR"). */
  profession: Profession | null;
  /** Finished games with a time. */
  timed: number;
  /** Quickest against its own par, on time alone (as show-core `eveningFor`). */
  fastest: GameRun | null;
  /** Slowest against its own par: only with two timed games or more, and never the fastest one. */
  slowest: GameRun | null;
  /** The game with the most mistakes, when there was at least one. */
  clumsiest: GameRun | null;
  /** Mistakes over every game that said how many. */
  totalMistakes: number;
  /** Games that said how many mistakes, and how many of them had none. */
  counted: number;
  cleanGames: number;
  /** Votes for one of the stop's saboteurs, from the reveals' own reasons. */
  rightVotes: number;
  /** The eras this guest was a saboteur in: their own `You.saboteurAt`, and the public reveals. */
  saboteurAt: Era[];
  /** Stops where, as a saboteur, they got away, or were caught. */
  escaped: number;
  caught: number;
  /** Damage their own alarms did as a saboteur (once per alarm). */
  damageDone: number;
  /** Times voted out, and how many of those they were innocent. */
  votedOut: number;
  wronglyOut: number;
  /**
   * Alarms whose public trace took in this guest's seat. The room sends a phone only its last few
   * alarms (show-core `wire.ts`), so this counts the ones the phone still has at the end.
   */
  nearAlarms: number;
  /** This guest found the hidden signal (their own `You.signal`). */
  signal: boolean;
}

/** Just what the stats read off the room, so the phone's own WireState fits as it is. */
export interface StatsSource {
  seats: readonly { seat: number; profession: Profession | null }[];
  tasks: Readonly<Record<string, Pick<TaskState, 'chapter' | 'index' | 'seat' | 'done' | 'ms' | 'mistakes'>>>;
  gameSet?: GameSet;
  game: {
    anomalies: readonly { chapter: number; near: readonly number[] }[];
    meetings?: readonly Pick<Meeting, 'chapter' | 'result'>[];
    reveals?: readonly Reveal[];
  };
}

/** Just what the stats read off this phone's own `You`. */
export interface StatsYou {
  profession?: Profession | null;
  signal?: boolean;
  saboteurAt?: readonly number[];
}

const isEra = (n: number): n is Era => n >= 1 && n <= 5 && Number.isInteger(n);

/** A finished game's pace against its par: above 1 is faster than the target. */
export function paceOf(run: Pick<GameRun, 'ms' | 'parMs'>): number {
  return run.ms === null ? 0 : run.parMs / Math.max(1, run.ms);
}

/** This guest's finished games, in the order they were played. */
export function gameRuns(state: Pick<StatsSource, 'tasks' | 'gameSet'>, seat: number): GameRun[] {
  const en = screenText('en');
  const no = screenText('no');
  const runs: GameRun[] = [];
  for (const task of Object.values(state.tasks)) {
    if (task.seat !== seat || !task.done || !isEra(task.chapter)) continue;
    const def = taskDef(task.chapter, task.index, state.gameSet);
    if (!def) continue;
    runs.push({
      chapter: task.chapter,
      index: task.index,
      title: { en: en.taskTitle(task.chapter, def), no: no.taskTitle(task.chapter, def) },
      ms: typeof task.ms === 'number' && task.ms > 0 ? task.ms : null,
      parMs: def.parMs,
      mistakes: typeof task.mistakes === 'number' && task.mistakes >= 0 ? task.mistakes : null,
    });
  }
  return runs.sort((a, b) => a.chapter - b.chapter || a.index - b.index);
}

interface Awards {
  rightVote: number;
  caught: number;
  wronglyOut: number;
  escaped: number;
  damage: number;
}

const NONE: Awards = { rightVote: 0, caught: 0, wronglyOut: 0, escaped: 0, damage: 0 };

const AWARD: Record<StopReason, keyof Awards> = {
  RIGHT_VOTE: 'rightVote',
  CAUGHT: 'caught',
  WRONGLY_OUT: 'wronglyOut',
  ESCAPED: 'escaped',
  DAMAGE: 'damage',
};

/**
 * What one stop's reveal gave this seat. The room says why (`Reveal.why`); a room from before says only
 * the points, so they are read back the way the reveal sheet does — without a ballot, which this phone
 * never has at the end: caught, voted out or got away from the verdict, then a crew guest's rest is a
 * right vote and a saboteur's is their alarms' damage. A rest that fits neither is left out.
 */
export function revealAwards(reveal: Reveal, seat: number, out: number | null): Awards {
  const key = String(seat);
  const why = reveal.why;
  if (why) {
    const got = { ...NONE };
    for (const reason of why[key] ?? []) got[AWARD[reason]] += 1;
    return got;
  }
  const got = { ...NONE };
  const agent = reveal.seats.includes(seat);
  let rest = reveal.points[key] ?? 0;
  if (out === seat) {
    if (agent) got.caught = 1;
    else got.wronglyOut = 1;
    rest -= agent ? STOP_POINTS.CAUGHT : STOP_POINTS.WRONGLY_OUT;
  } else if (agent) {
    got.escaped = 1;
    rest -= STOP_POINTS.ESCAPED;
  }
  if (!agent && rest === STOP_POINTS.RIGHT_VOTE) got.rightVote = 1;
  if (agent && rest > 0 && rest % STOP_POINTS.DAMAGE === 0) got.damage = rest / STOP_POINTS.DAMAGE;
  return got;
}

/** Who a stop's own meeting voted out, if anyone: its last counted meeting. */
function votedOutAt(meetings: StatsSource['game']['meetings'], chapter: number): Pick<Meeting, 'result'> | null {
  const counted = (meetings ?? []).filter((m) => m.chapter === chapter && m.result !== null);
  return counted[counted.length - 1] ?? null;
}

/** Everything the evening's cards and video say about this guest, beyond the scores. */
export function wrappedStats(state: StatsSource, seat: number, you?: StatsYou | null): WrappedStats {
  const runs = gameRuns(state, seat);
  const timed = runs.filter((r) => r.ms !== null);
  let fastest: GameRun | null = null;
  let slowest: GameRun | null = null;
  for (const run of timed) {
    if (!fastest || paceOf(run) > paceOf(fastest)) fastest = run;
    if (!slowest || paceOf(run) < paceOf(slowest)) slowest = run;
  }
  if (timed.length < 2 || slowest === fastest) slowest = null;

  const counted = runs.filter((r) => r.mistakes !== null);
  let clumsiest: GameRun | null = null;
  for (const run of counted) if ((run.mistakes ?? 0) > (clumsiest?.mistakes ?? 0)) clumsiest = run;

  const sum: Awards = { ...NONE };
  const outAt = new Set<number>();
  const sabotaged = new Set<Era>((you?.saboteurAt ?? []).filter(isEra));
  const meetings = state.game.meetings;
  for (const reveal of state.game.reveals ?? []) {
    const out = votedOutAt(meetings, reveal.chapter)?.result?.sentToBrig ?? null;
    const got = revealAwards(reveal, seat, out);
    for (const k of Object.keys(sum) as (keyof Awards)[]) sum[k] += got[k];
    if (got.caught + got.wronglyOut > 0) outAt.add(reveal.chapter);
    if (reveal.seats.includes(seat) && isEra(reveal.chapter)) sabotaged.add(reveal.chapter);
  }
  // A meeting that voted this guest out at a stop with no reveal on this phone: the verdict says which.
  const revealed = new Set((state.game.reveals ?? []).map((r) => r.chapter));
  for (const m of meetings ?? []) {
    if (revealed.has(m.chapter) || outAt.has(m.chapter) || m.result?.sentToBrig !== seat) continue;
    outAt.add(m.chapter);
    if (m.result.caughtAgent) sum.caught += 1;
    else sum.wronglyOut += 1;
  }

  const nearAlarms = state.game.anomalies.filter((a) => {
    const [lo, hi] = a.near;
    return a.chapter >= 1 && lo !== undefined && hi !== undefined && seat >= lo && seat <= hi;
  }).length;

  return {
    profession: you?.profession ?? state.seats.find((s) => s.seat === seat)?.profession ?? null,
    timed: timed.length,
    fastest,
    slowest,
    clumsiest,
    totalMistakes: counted.reduce((n, r) => n + (r.mistakes ?? 0), 0),
    counted: counted.length,
    cleanGames: counted.filter((r) => r.mistakes === 0).length,
    rightVotes: sum.rightVote,
    saboteurAt: [...sabotaged].sort((a, b) => a - b),
    escaped: sum.escaped,
    caught: sum.caught,
    damageDone: sum.damage,
    votedOut: sum.caught + sum.wronglyOut,
    wronglyOut: sum.wronglyOut,
    nearAlarms,
    signal: you?.signal === true,
  };
}

// ---------------------------------------------------------------------------
// The funny part: a title for the evening, and the lines that only show when they are worth a smile.

/** When a funny line is worth showing. Kind numbers, so nobody is teased for one slip. */
export const FUNNY = {
  /** Mistakes in one game before "butter fingers". */
  CLUMSY: 3,
  /** …and before "the stubborn one" as a title. */
  STUBBORN: 5,
  /** Alarms near your seat before "the usual suspect" as a title. */
  SUSPECT: 3,
  /** Faster than the target by this much for "speed demon". */
  SPEED: 1.5,
  /** Right votes for "sharp eye", alarms fixed for "the fixer". */
  SHARP: 2,
  FIXER: 2,
  /** Games counted, all without a mistake, for "the calm one". */
  CALM: 2,
  /** Messages sent for "the chatterbox". */
  CHATTY: 15,
  /** Faster than the target by this much for a job done fast. */
  JOB_FAST: 1.3,
} as const;

export type CrewTitle =
  | 'masterSaboteur'
  | 'slipperySaboteur'
  | 'boldSaboteur'
  | 'ace'
  | 'speedDemon'
  | 'sharpEye'
  | 'fixer'
  | 'chatterbox'
  | 'misunderstood'
  | 'calm'
  | 'stubborn'
  | 'usualSuspect'
  | 'loyal';

export const CREW_TITLES: readonly CrewTitle[] = [
  'masterSaboteur',
  'slipperySaboteur',
  'boldSaboteur',
  'ace',
  'speedDemon',
  'sharpEye',
  'fixer',
  'chatterbox',
  'misunderstood',
  'calm',
  'stubborn',
  'usualSuspect',
  'loyal',
];

/**
 * The guest's title for the evening, the first that fits, rarest first. Always kind: a guest caught as a
 * saboteur is bold, one voted out wrongly is misunderstood, one with a lot of mistakes is stubborn.
 */
export function crewTitle(
  summary: Pick<EveningSummary, 'rank' | 'total' | 'players' | 'fixes' | 'fastest'>,
  stats: WrappedStats | null | undefined,
  saboteurAt: readonly number[] = [],
  chat?: ChatCounts | null,
): CrewTitle {
  const stops = Math.max(stats?.saboteurAt.length ?? 0, saboteurAt.filter(isEra).length);
  if (stops > 0) {
    const caught = stats?.caught ?? 0;
    if (caught === 0) return 'masterSaboteur';
    return (stats?.escaped ?? 0) > 0 ? 'slipperySaboteur' : 'boldSaboteur';
  }
  // Never the hidden signal: the Wrapped is made to be shared, and a post that says there is a signal to find
  // would tell the next evenings' guests (signal.ts: kept secret, Marek).
  if (summary.rank === 1 && summary.total > 0 && summary.players > 1) return 'ace';
  const fast = stats?.fastest ?? (summary.fastest ? { ms: summary.fastest.ms, parMs: summary.fastest.parMs } : null);
  if (fast && paceOf(fast) >= FUNNY.SPEED) return 'speedDemon';
  if ((stats?.rightVotes ?? 0) >= FUNNY.SHARP) return 'sharpEye';
  if (summary.fixes >= FUNNY.FIXER) return 'fixer';
  if ((chat?.sent ?? 0) >= FUNNY.CHATTY) return 'chatterbox';
  if ((stats?.wronglyOut ?? 0) > 0) return 'misunderstood';
  if (stats && stats.counted >= FUNNY.CALM && stats.totalMistakes === 0) return 'calm';
  if ((stats?.clumsiest?.mistakes ?? 0) >= FUNNY.STUBBORN) return 'stubborn';
  if ((stats?.nearAlarms ?? 0) >= FUNNY.SUSPECT) return 'usualSuspect';
  return 'loyal';
}

/** One row of the funny card: a big number (or time) and the words under it. */
export type FunnyRow =
  | { kind: 'wronglyOut'; n: number }
  | { kind: 'clumsy'; run: GameRun }
  | { kind: 'slow'; run: GameRun }
  | { kind: 'near'; n: number }
  | { kind: 'clean'; n: number };

/** The funny card's rows, most worth a smile first, three at most; empty when there is nothing to say. */
export function funnyRows(stats: WrappedStats | null | undefined): FunnyRow[] {
  if (!stats) return [];
  const rows: FunnyRow[] = [];
  if (stats.wronglyOut > 0) rows.push({ kind: 'wronglyOut', n: stats.wronglyOut });
  const clumsy = stats.clumsiest && (stats.clumsiest.mistakes ?? 0) >= FUNNY.CLUMSY ? stats.clumsiest : null;
  if (clumsy) rows.push({ kind: 'clumsy', run: clumsy });
  // One game named twice reads as a list, not a joke: the slowest only when it is another game.
  const same = clumsy && stats.slowest && clumsy.chapter === stats.slowest.chapter && clumsy.index === stats.slowest.index;
  if (stats.slowest && !same) rows.push({ kind: 'slow', run: stats.slowest });
  if (stats.nearAlarms > 0) rows.push({ kind: 'near', n: stats.nearAlarms });
  if (stats.cleanGames > 0) rows.push({ kind: 'clean', n: stats.cleanGames });
  return rows.slice(0, 3);
}

// ---------------------------------------------------------------------------
// The guest's own messages, in counts, and their job.

/** This phone's own messages tonight, counted: never a word of them, never who they were with. */
export interface ChatCounts {
  sent: number;
  received: number;
  /** How many different guests they wrote to or heard from, one to one. */
  people: number;
}

/**
 * Counts from the messages the room sent this phone alone (chat/store.ts): its own conversations, and the
 * saboteurs' channel if it was ever in it. Only numbers leave here.
 */
export function chatCounts(messages: readonly Pick<WireChat, 'from' | 'to'>[], seat: number): ChatCounts {
  let sent = 0;
  let received = 0;
  const people = new Set<number>();
  for (const m of messages) {
    if (m.from === seat) sent += 1;
    else received += 1;
    // One to one only: the saboteurs' channel and the whole table's are not "people".
    if (m.to === SABOTEURS || m.to === EVERYONE) continue;
    const other = m.from === seat ? m.to : m.from;
    if (typeof other === 'number' && other !== seat) people.add(other);
  }
  return { sent, received, people: people.size };
}

/** How the guest's post went, for its slide's line: fast, without a mistake, the long way round, or not at all. */
export type JobMood = 'fast' | 'clean' | 'scenic' | 'none';

export function jobMood(games: number, stats: WrappedStats | null | undefined): JobMood {
  if (games <= 0) return 'none';
  if (stats?.fastest && paceOf(stats.fastest) >= FUNNY.JOB_FAST) return 'fast';
  if (stats && stats.counted >= FUNNY.CALM && stats.totalMistakes === 0) return 'clean';
  return 'scenic';
}
